/**
 * Admin context bar - cross-FP drawer only. A thin bar pinned at the very top
 * of the drawer, ABOVE the shared AppDrawerHeader, that carries the FP scope
 * (market chip + franchise name), the "View report" jump, and the close
 * control. Demarcated by a single 0.5px bottom hairline on white - no grey
 * fill, so it never reintroduces the off-white body band.
 *
 * The shared header below it is byte-identical to the inspector drawer; admin
 * surfaces simply prepend this bar (and the parent hides the header's own close
 * so only this bar's close shows). Inspector views leave ``fpContext``
 * undefined and this renders ``null``.
 */
import React from 'react'
import { Box, Chip, IconButton, Link, Typography } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import CloseIcon from '@mui/icons-material/Close'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { getCountryChipSx } from '../../../../utils/countryChips'
import { poolwerxColors } from '../../../../theme/colors'

interface AdminContextBarProps {
  fpContext?: { franchise_name: string; country: string } | null
  /** Inspector report deep-link for this row. When set, a "View report"
   *  jump renders before the close control. */
  reportHref?: string
  onClose: () => void
}

const AdminContextBar: React.FC<AdminContextBarProps> = ({
  fpContext,
  reportHref,
  onClose,
}) => {
  if (!fpContext) return null
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: '22px',
        py: '8px',
        bgcolor: poolwerxColors.surface.primary,
        borderBottom: `0.5px solid ${poolwerxColors.border.tertiary}`,
      }}
    >
      <Chip
        label={fpContext.country}
        size="small"
        variant="outlined"
        sx={{
          ...getCountryChipSx(fpContext.country),
          height: 18,
          flexShrink: 0,
          '& .MuiChip-label': {
            px: 0.625,
            fontSize: '0.6rem',
            fontWeight: 600,
            letterSpacing: '0.02em',
          },
        }}
      />
      <Typography
        variant="body2"
        title={fpContext.franchise_name}
        sx={{
          flex: 1,
          minWidth: 0,
          fontWeight: 600,
          lineHeight: 1.2,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {fpContext.franchise_name}
      </Typography>
      {reportHref && (
        <Link
          component={RouterLink}
          to={reportHref}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.375,
            flexShrink: 0,
            fontSize: '12.5px',
            fontWeight: 500,
            color: poolwerxColors.teal,
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          View report
          <OpenInNewIcon sx={{ fontSize: 13 }} />
        </Link>
      )}
      <IconButton
        size="small"
        onClick={onClose}
        aria-label="Close"
        sx={{ flexShrink: 0, ml: 0.5 }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  )
}

export default AdminContextBar
